import { Encounter, Location } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart, SmartCondition } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import { toTitleCase } from "@metriport/shared";
import { buildDayjs } from "@metriport/shared/common/date";

export type EncounterRowData = {
  id: string;
  reasons: string;
  type: string;
  location: string;
  startDate: string;
  endDate: string;
  originalData: Smart<Encounter>;
};

export function getEncounterRows({ bundle }: { bundle: FhirBundleSdk }): EncounterRowData[] {
  const encounters = bundle.getEncounters();
  const locations = bundle.getLocations();

  return encounters
    .map(encounter => ({
      id: encounter.id ?? "-",
      reasons: getEncounterDisplay(encounter),
      location: getEncounterLocation(encounter, locations),
      type: renderClassDisplay(encounter),
      startDate: formatDate(encounter.period?.start),
      endDate: formatDate(encounter.period?.end),
      originalData: encounter,
    }))
    .sort((a, b) => {
      const dateA = buildDayjs(a.endDate !== "-" ? a.endDate : a.startDate);
      const dateB = buildDayjs(b.endDate !== "-" ? b.endDate : b.startDate);
      return dateB.diff(dateA); // newest first
    });
}

function getEncounterDisplay(encounter: Smart<Encounter>): string {
  const reasonDisplays =
    encounter.reasonCode
      ?.map(reason => reason.text ?? reason.coding?.[0]?.display)
      .filter((value): value is string => Boolean(value)) ?? [];

  if (reasonDisplays.length > 0) {
    return reasonDisplays.join(", ");
  }

  const diagnosisDisplays = encounter
    .getDiagnosisConditions()
    .filter((resource): resource is SmartCondition => resource.resourceType === "Condition")
    .map(condition => {
      const coding = condition.code?.findIcd10Coding(code => code.toUpperCase().startsWith("Z"));
      return coding?.display;
    })
    .filter((value): value is string => Boolean(value));

  return diagnosisDisplays.length > 0 ? diagnosisDisplays.join(", ") : "-";
}

function getEncounterLocation(encounter: Encounter, locations: Location[]): string {
  const locationId = encounter.location?.[0]?.location?.reference?.split("/")?.[1];
  const location = locations.find(l => l.id === locationId);
  return location?.name || "-";
}

function renderClassDisplay(encounter: Encounter): string {
  const isUsefulDisplay = (display: string | undefined) =>
    display !== undefined && display !== "unknown";

  if (encounter.class?.display && isUsefulDisplay(encounter.class.display)) {
    return normalizeDisplay(encounter.class.display);
  } else if (encounter.class?.extension) {
    const extension = encounter.class.extension.find(coding => {
      return coding.valueCoding?.code === encounter.class?.code;
    });
    return extension?.valueCoding?.display ? normalizeDisplay(extension.valueCoding.display) : "-";
  } else if (encounter.type) {
    const allTypeStringSet = new Set<string>();

    for (const type of encounter.type) {
      if (type.text && isUsefulDisplay(type.text)) {
        allTypeStringSet.add(normalizeDisplay(type.text));
      }
      type.coding?.forEach(c => {
        if (c.display) allTypeStringSet.add(c.display);
      });
    }
    return Array.from(allTypeStringSet).join(", ");
  }

  return "-";
}

function normalizeDisplay(str: string): string {
  return toTitleCase(str.trim());
}

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";
  try {
    const parsed = buildDayjs(date);
    if (!parsed.isValid()) return "-";
    return parsed.format(ISO_DATE);
  } catch {
    return "-";
  }
}
